import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import { Radio } from "@base-ui/react/radio";
import { RadioGroup as BaseRadioGroup } from "@base-ui/react/radio-group";
import { IconCheck, IconMinus } from "@tabler/icons-react";
import type { ReactNode } from "react";

/**
 * A checkbox on Base UI's Checkbox, in place of `<input type="checkbox">`, with its label beside it (the
 * whole label toggles it). Without `label`, give it an `aria-label` (a checkbox in a table cell).
 */
export interface CheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  /** Some but not all of a group are checked (a "select all" box). */
  indeterminate?: boolean;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  "aria-label"?: string;
  title?: string;
  className?: string;
}

export function Checkbox({ checked, onCheckedChange, label, indeterminate, disabled, required, name, value, className, title, ...rest }: CheckboxProps) {
  const box = (
    <BaseCheckbox.Root
      checked={checked}
      onCheckedChange={(c) => onCheckedChange(c)}
      indeterminate={indeterminate}
      disabled={disabled}
      required={required}
      name={name}
      value={value}
      aria-label={rest["aria-label"]}
      title={label ? undefined : title}
      className={label ? "cbx" : `cbx${className ? ` ${className}` : ""}`}
    >
      <BaseCheckbox.Indicator className="cbx-mark" keepMounted>
        {indeterminate ? <IconMinus size={13} stroke={3} /> : <IconCheck size={13} stroke={3} />}
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );
  if (!label) return box;
  return (
    <label className={`check${disabled ? " disabled" : ""}${className ? ` ${className}` : ""}`} title={title}>
      {box}
      <span>{label}</span>
    </label>
  );
}

export interface RadioOption<V extends string> {
  value: V;
  label: ReactNode;
  /** A line under the label. */
  hint?: ReactNode;
  disabled?: boolean;
}

/**
 * Radio buttons on Base UI's RadioGroup: one Tab stop, arrow keys move and choose. For a short choice
 * shown as a row of buttons, a `ToggleGroup` usually reads better; radios suit a choice with explanations.
 */
export interface RadioGroupProps<V extends string> {
  value: V;
  onValueChange: (value: V) => void;
  options: readonly RadioOption<V>[];
  /** What is being chosen, for screen readers (outside a Field). */
  label: string;
  /** Side by side instead of one per line. */
  inline?: boolean;
  disabled?: boolean;
  name?: string;
  /** Extra classes on the group, for an app's own option look (cards, list rows). */
  className?: string;
}

export function RadioGroup<V extends string>({ value, onValueChange, options, label, inline, disabled, name, className }: RadioGroupProps<V>) {
  return (
    <BaseRadioGroup value={value} onValueChange={(v) => onValueChange(v as V)} disabled={disabled} name={name} aria-label={label} className={`radios${inline ? " inline" : ""}${className ? ` ${className}` : ""}`}>
      {options.map((o) => (
        <label key={o.value} className={`radio${o.disabled ? " disabled" : ""}`}>
          <Radio.Root value={o.value} disabled={o.disabled} className="radio-dot">
            <Radio.Indicator className="radio-mark" />
          </Radio.Root>
          <span>
            {o.label}
            {o.hint && <span className="radio-hint">{o.hint}</span>}
          </span>
        </label>
      ))}
    </BaseRadioGroup>
  );
}
